/**
 * One place that turns an API error into the sentence a person should read.
 * The API attaches a machine-readable `code` to the refusals a form can act
 * on (docs/SCHEMA.md § Account lifecycle & abuse controls); everything else
 * falls back to the API's own message, then to the caller's fallback.
 */

import { retryAfterMs } from './retryAfter';
import { planLimitOf, planLimitSentence } from './planLimit';
import { GRANT_READ_ONLY_WORDS, isGrantReadOnly } from './elementGrantRefusals';

/** A Retry-After in words: "30 minutes", "2 hours", "45 seconds". */
export const formatWait = seconds => {
	if (seconds >= 3600) {
		const h = Math.ceil(seconds / 3600);
		return `${h} hour${h === 1 ? '' : 's'}`;
	}
	if (seconds >= 60) {
		const m = Math.ceil(seconds / 60);
		return `${m} minute${m === 1 ? '' : 's'}`;
	}
	return `${Math.max(1, Math.ceil(seconds))} second${seconds === 1 ? '' : 's'}`;
};

const BY_CODE = {
	challenge_failed: 'Please complete the verification challenge and try again.',
	challenge_unavailable: 'The verification service is unavailable right now. Try again in a moment.',
	email_unverified: 'Confirm your email address to do this. You can resend the confirmation email from your profile.',
	registration_closed: 'Registration is closed at the moment.',
	email_disposable: 'Temporary or disposable email addresses cannot be used. Please use a permanent address.',
	blocked: 'This action is not available for this address.',
	count_mismatch: 'The list changed since you loaded it. Refresh and try again.',
	// The account link on a card a group built from records reads (keepr-api
	// docs/SCHEMA.md § Dynamic groups, owner ruling b76): linking someone there
	// needs the group's access across the whole collection.
	account_link_needs_access: 'Linking an account here adds that person to a group with more access than you have. Ask a manager.',
};

// The same refusal when the link stays and the write changes something a
// group's rule reads — "Staff where role is Nurse" (keepr-api owner ruling
// b78): the API marks it with `changed` ('element', 'tags', 'notes' or
// 'updated') and names the changed element in `element`.
export const ACCOUNT_LINK_FIELD_WORDS = 'This change can put the person linked to this item in a group with more access than you have. Ask a manager.';
// …and when the field is one such a rule reads THROUGH this record — a
// department's name, a record a total counts (owner ruling b79): `changed`
// is 'related', and the people it reaches are linked to OTHER records.
export const ACCOUNT_LINK_RELATED_WORDS = 'This change can bring people into a group with more access than you have. Ask a manager.';

export function apiErrorCode(error) {
	return error?.response?.data?.code || null;
}

export function isApiCode(error, code) {
	return apiErrorCode(error) === code;
}

/** Seconds the API asked us to wait on a 429, or null. */
export function retryAfterSeconds(error) {
	if (error?.response?.status !== 429) return null;
	const ms = retryAfterMs(error, { body: true });
	return ms !== null && ms > 0 ? ms / 1000 : null;
}

export function apiErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
	if (!error?.response) {
		return "We couldn't reach the server. Check your connection and try again.";
	}
	const wait = retryAfterSeconds(error);
	if (error.response.status === 429) {
		return wait
			? `Too many attempts. Try again in ${formatWait(wait)}.`
			: 'Too many attempts. Try again in a little while.';
	}
	const data = error.response.data || {};
	// A plan limit, worded for whoever met it (utils/planLimit.js): the owner,
	// a member, or a visitor — read from what the API chose to send them.
	const planLimit = planLimitOf(error);
	if (planLimit) return planLimitSentence(planLimit);
	// Refusals that carry their own specific sentence keep it.
	if (data.code === 'name_invalid' || data.code === 'email_undeliverable') {
		return data.message || BY_CODE[data.code] || fallback;
	}
	if (isGrantReadOnly(data)) return GRANT_READ_ONLY_WORDS;
	if (data.code === 'account_link_needs_access' && data.changed === 'related') return ACCOUNT_LINK_RELATED_WORDS;
	if (data.code === 'account_link_needs_access' && data.changed) return ACCOUNT_LINK_FIELD_WORDS;
	if (data.code && BY_CODE[data.code]) return BY_CODE[data.code];
	return typeof data.message === 'string' && data.message ? data.message : fallback;
}
